import {
  Badge, Button, Drawer, Menu, Modal, NativeSelect, Popover, Select, Table, TextInput, Tooltip,
  createTheme, type CSSVariablesResolver, type MantineColorsTuple,
} from "@mantine/core";

/* Mantine's theme, built from the tokens in docs/style-guide.md.
 *
 * The values are Buzze's, read off buzze.biz and checked for contrast. The
 * same values are the custom properties at the top of src/styles.css, and the
 * resolver at the bottom of this file points Mantine's own variables at ours,
 * so one token reaches both the kit and our own CSS. docs/design-standards.md,
 * Brand and visual language, says why: a kit's default theme is nobody's brand.
 *
 * Shade 7 of navy is the brand navy, and the lighter shades are the site's
 * surfaces and borders. Shade 5 of orange is the brand orange, which fails as
 * text on white (1.9:1), so it is only ever a fill behind navy text or an
 * underline on the navy bar. */
const navy: MantineColorsTuple = [
  "#F1F6FA", "#E4EEF5", "#D9E5EE", "#C9DCE9", "#9DBDD2",
  "#6F8A9E", "#3E5A6E", "#054063", "#0E2A3D", "#03293F",
];
const orange: MantineColorsTuple = [
  "#FFF4DE", "#FBE6BF", "#F5D9A5", "#FFCE85", "#FFBE5C",
  "#FAAF41", "#E89A22", "#B05A1E", "#7A5310", "#5C3E0C",
];

export const FONT = "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif";

/* Motion is off at the root and brought back where it communicates. The
   drawer keeps its slide, because it says where the record came from. Menus,
   popovers and tooltips appear in place. */
const still = { transitionProps: { duration: 0 } };

export const theme = createTheme({
  primaryColor: "navy",
  primaryShade: 7,
  colors: { navy, orange },
  white: "#FFFFFF",
  black: "#0E2A3D",
  fontFamily: FONT,
  fontSizes: { xs: "12px", sm: "13.5px", md: "14px", lg: "16px", xl: "18px" },
  lineHeights: { xs: "1.4", sm: "1.45", md: "1.45", lg: "1.5", xl: "1.5" },
  headings: {
    fontFamily: FONT,
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "1.25rem", lineHeight: "1.3" },
      h2: { fontSize: "1.05rem", lineHeight: "1.3" },
      h3: { fontSize: "0.95rem", lineHeight: "1.35" },
      h4: { fontSize: "13px", lineHeight: "1.4" },
    },
  },
  // 12px for everything that is not a pill, and the pill for buttons and chips.
  defaultRadius: "md",
  radius: { xs: "4px", sm: "8px", md: "12px", lg: "16px", xl: "999px" },
  // The one shadow, for things that float over the page.
  shadows: { md: "0 10px 30px rgba(5, 64, 99, 0.10)" },
  respectReducedMotion: true,
  components: {
    Button: Button.extend({ defaultProps: { radius: "xl", size: "sm" } }),
    Badge: Badge.extend({ defaultProps: { radius: "xl", variant: "outline", size: "sm", tt: "none", fw: 600 } }),
    TextInput: TextInput.extend({ defaultProps: { size: "sm", radius: "md" } }),
    NativeSelect: NativeSelect.extend({ defaultProps: { size: "sm", radius: "md" } }),
    Select: Select.extend({ defaultProps: { size: "sm", radius: "md", comboboxProps: { shadow: "md", ...still } } }),
    Menu: Menu.extend({ defaultProps: { shadow: "md", radius: "md", ...still } }),
    Popover: Popover.extend({ defaultProps: { shadow: "md", radius: "md", ...still } }),
    Tooltip: Tooltip.extend({ defaultProps: still }),
    Modal: Modal.extend({ defaultProps: { radius: "md", shadow: "md", ...still } }),
    Drawer: Drawer.extend({ defaultProps: { transitionProps: { duration: 180, transition: "slide-left" } } }),
    // The row padding the lists use today, so a rebuilt table keeps its density.
    Table: Table.extend({ defaultProps: { verticalSpacing: "0.34rem", horizontalSpacing: "0.55rem" } }),
  },
});

/* Mantine's variables, pointed at ours where the two name the same thing. */
export const cssVariables: CSSVariablesResolver = () => ({
  variables: {
    "--mantine-color-body": "var(--page)",
    "--mantine-color-text": "var(--text)",
    "--mantine-color-dimmed": "var(--text-muted)",
    "--mantine-color-default-border": "var(--control-edge)",
    "--mantine-color-anchor": "var(--heading)",
  },
  light: {},
  dark: {},
});
